import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { to: "/", label: "首页", end: true },
  { to: "/movies", label: "电影", end: false },
  { to: "/short-videos", label: "短视频", end: false },
  { to: "/about", label: "关于我们", end: false },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [focusSearch, setFocusSearch] = useState(false);
  const mobileSearchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!menuOpen || !focusSearch) return;
    const timer = window.setTimeout(() => {
      mobileSearchRef.current?.focus();
      setFocusSearch(false);
    }, 120);
    return () => window.clearTimeout(timer);
  }, [menuOpen, focusSearch]);

  useEffect(() => {
    if (location.pathname === "/search") {
      const params = new URLSearchParams(location.search);
      setKeyword(params.get("q") ?? "");
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 12);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) {
      navigate("/search");
      return;
    }
    navigate(`/search?q=${encodeURIComponent(value)}`);
    setMenuOpen(false);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
        scrolled
          ? "border-background-800/90 bg-background-950/95 backdrop-blur-xl"
          : "border-background-800/40 bg-background-950/70 backdrop-blur-md"
      }`}
    >
      <div className="flex h-16 w-full items-center gap-4 px-4 md:h-[72px] md:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-500 text-background-950">
            <i className="ri-moon-fill text-lg" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-bold tracking-wide text-foreground-50">午夜视频</span>
            <span className="mt-1 font-label text-[10px] tracking-[0.28em] text-foreground-500">
              MIDNIGHT VIDEO
            </span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 ml-4">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-md px-3.5 py-2 text-sm transition-colors ${
                  isActive
                    ? "bg-primary-900/60 text-accent-400"
                    : "text-foreground-300 hover:bg-background-800/70 hover:text-foreground-50"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <form onSubmit={handleSearch} className="hidden md:block">
            <div className="group flex h-10 items-center gap-2 rounded-full border border-background-700/80 bg-background-900/80 pl-4 pr-1 transition-colors focus-within:border-accent-500/70">
              <i className="ri-search-line text-base text-foreground-500" />
              <input
                type="text"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影片、短视频…"
                aria-label="搜索影片或短视频"
                className="w-40 bg-transparent text-sm text-foreground-100 outline-none transition-all placeholder:text-foreground-500 focus:w-56"
              />
              <button
                type="submit"
                aria-label="开始搜索"
                className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-700 text-foreground-100 transition-colors hover:bg-accent-500 hover:text-background-950"
              >
                <i className="ri-arrow-right-line text-sm" />
              </button>
            </div>
          </form>

          <button
            type="button"
            onClick={() => {
              setMenuOpen(true);
              setFocusSearch(true);
            }}
            aria-label="打开搜索"
            className="flex h-10 w-10 items-center justify-center rounded-md border border-background-700 text-foreground-100 transition-colors hover:border-accent-500/60 hover:text-accent-400 md:hidden"
          >
            <i className="ri-search-line text-lg" />
          </button>

          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-label={menuOpen ? "关闭菜单" : "打开菜单"}
            aria-expanded={menuOpen}
            className="flex h-10 w-10 items-center justify-center rounded-md border border-background-700 text-foreground-100 md:hidden"
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="border-t border-background-800 bg-background-950 px-4 pb-5 pt-4 md:hidden">
          <form onSubmit={handleSearch} className="mb-4">
            <div className="flex h-11 items-center gap-2 rounded-lg border border-background-700 bg-background-900 px-3">
              <i className="ri-search-line text-base text-foreground-500" />
              <input
                ref={mobileSearchRef}
                type="text"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影片、短视频…"
                aria-label="搜索影片或短视频"
                className="w-full bg-transparent text-sm text-foreground-100 outline-none placeholder:text-foreground-500"
              />
              <button
                type="submit"
                aria-label="开始搜索"
                className="whitespace-nowrap rounded-md bg-accent-500 px-3 py-1.5 text-xs font-medium text-background-950"
              >
                搜索
              </button>
            </div>
          </form>
          <div className="flex flex-col gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center justify-between rounded-md px-3 py-3 text-sm ${
                    isActive
                      ? "bg-primary-900/60 text-accent-400"
                      : "text-foreground-200 hover:bg-background-800/70"
                  }`
                }
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-base text-foreground-500" />
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}